import { useI18n } from '@muse/i18n'
import { useIsHydrating } from '@muse-web/router'
import { useIsTabletUp } from '@core/media'

import { type AnalyticsPlacementTypes, sendEvent } from '@core/analytics'
import { shortCount } from '@core/format'
import { getCategoryById, type TAudioSimple, type TPlaylist } from '@core/models'
import { getAuthorPageUrl, getSearchCategoryUrl } from '@core/routes'
import { AddToListIcon, ShareIcon, UserAddedIcon, UserAddIcon } from '@ds/components'
import {
    AudioPlaybackTimeContainer,
    WaveformContainer,
    PlayButtonContainer,
} from '@features/audio-player'
import {
    canDownloadAudio,
    DownloadButtonContainer,
    useDownloadDialog,
} from '@features/audio-download'
import {
    buildCollectionTagLinks,
    type TOrderableList,
    useAddToCollection,
} from '@features/collection'
import { type TShareAudioEntity, useShareDialog } from '@features/share'
import { useCurrentUser } from '@features/session'
import { FollowButtonContainer, useFollowAction } from '@features/follow'

import { LikeButtonContainer } from '@features/audio-like'
import { isAudioOwner, getAudioPageUrl } from '@features/audio'
import {
    AudioCard,
    AudioCardActionsMenu,
    AudioCardTags,
    type TAudioCardMenuItem,
    type TAudioCardVariant,
} from '../components/audio-card'
import { AudioPlayStats } from '../components/audio-play-stats'

import { AudioCardOwnerActionsContainer } from './AudioCardOwnerActionsContainer'

const MAX_VISIBLE_TAGS = 3

export type TAudioCardContainerProps = {
    audio: TAudioSimple
    placement: AnalyticsPlacementTypes
    /** Queue the card plays into. A list passes the audios it renders; a lone card passes nothing. */
    playlist?: TPlaylist
    variant?: TAudioCardVariant
    /** `false` drops the inline actions, the MORE menu and Follow — a listen-only card. */
    withActions?: boolean
    orderableList?: TOrderableList
}

export const AudioCardContainer = ({
    audio,
    placement,
    playlist,
    variant,
    withActions = true,
    orderableList,
}: TAudioCardContainerProps) => {
    const { t } = useI18n()
    const { username: myUsername, id: myId } = useCurrentUser()
    const share = useShareDialog()
    const addToCollection = useAddToCollection()
    const download = useDownloadDialog()
    const followAction = useFollowAction({
        username: audio.authorUsername,
        creatorUserId: audio.authorId,
        placement,
    })

    const isOwner = isAudioOwner(audio, myUsername)
    const isAuthenticated = myId !== 0
    const isHydrating = useIsHydrating()
    const isTabletUp = useIsTabletUp()
    const rendersAsOwner = isOwner && !isHydrating

    const handleTitleClick = () => {
        sendEvent({ name: 'audio_title_click', payload: {} })
    }

    const handleAuthorClick = () => {
        sendEvent({ name: 'audio_author_click', payload: {} })
    }

    const handleShare = () => {
        const entity: TShareAudioEntity = {
            item: 'audio',
            id: audio.id,
            title: audio.title,
            placement,
            authorUsername: audio.authorUsername,
            audioSlug: audio.slug,
            isOwner,
        }

        sendEvent({
            name: 'share_button_click',
            payload: {
                audio: audio.id,
                params__key: ['player_placement'],
                params__str_value: [placement],
            },
        })
        share.open(entity)
    }

    const handleAddToCollection = () => {
        addToCollection.open({
            id: audio.id,
            title: audio.title,
            authorUsername: audio.authorUsername,
        })
    }

    const canDownload = canDownloadAudio(audio, rendersAsOwner ? myUsername : undefined)

    const handleDownload = () => {
        download.open(
            {
                id: audio.id,
                title: audio.title,
                authorUsername: audio.authorUsername,
                authorId: audio.authorId,
                avatarUrl: audio.avatarUrl,
            },
            placement,
        )
    }

    // Viewer's MORE list; the owner's lives in AudioCardOwnerActionsContainer, along with the
    // replace/delete machinery it needs — mounting that per card would cost every browse page.
    const viewerMenuItems: TAudioCardMenuItem[] = [
        {
            id: 'share',
            label: t('i18n.audioCardShare'),
            Icon: ShareIcon,
            onSelect: handleShare,
        },
        ...(isAuthenticated
            ? [
                  {
                      id: 'add-to-collection',
                      label: t('i18n.audioCardAddToCollection'),
                      Icon: AddToListIcon,
                      onSelect: handleAddToCollection,
                  },
              ]
            : []),
        ...(followAction.isOwnProfile
            ? []
            : [
                  {
                      id: 'follow',
                      label: followAction.label,
                      Icon: followAction.isFollowing ? UserAddedIcon : UserAddIcon,
                      onSelect: followAction.toggle,
                  },
              ]),
    ]

    // The mobile sheet keeps share + add-to-collection only: following is the card's own control there,
    // as in audio-web (`MORE_ACTIONS_MOBILE`).
    const viewerSheetItems = viewerMenuItems.filter(item => item.id !== 'follow')

    // audio-web `AudioTags`: the unlisted icon, then the category chip, then up to three tags — a tag
    // is a link only when the audio cross-links it.
    const { links, extraCount } = buildCollectionTagLinks(
        audio.tags,
        audio.crossLinkTags,
        MAX_VISIBLE_TAGS,
    )
    const category = getCategoryById(audio.categoryId)

    const tagsSlot = (
        <AudioCardTags
            isUnlisted={!audio.isPublic}
            categoryTitle={category?.title}
            categoryHref={category ? getSearchCategoryUrl(category.name) : undefined}
            links={links}
            extraCount={extraCount}
        />
    )

    // Owner: edit + share inline. Viewer: download + like. Matches audio-web's INLINE action lists.
    const actionsSlot = rendersAsOwner ? (
        <AudioCardOwnerActionsContainer
            audio={audio}
            canDownload={canDownload}
            orderableList={orderableList}
            onShare={handleShare}
            onAddToCollection={handleAddToCollection}
            onDownload={handleDownload}
        />
    ) : (
        <>
            {canDownload ? <DownloadButtonContainer audio={audio} placement={placement} /> : null}
            <LikeButtonContainer
                audioId={audio.id}
                placement={placement}
                likesCount={audio.likesCount}
            />
            <AudioCardActionsMenu
                items={viewerMenuItems}
                sheetItems={viewerSheetItems}
                triggerLabel={t('i18n.audioCardMoreActions')}
                sheetTitle={t('i18n.audioActionsSheetTitle')}
                cancelLabel={t('i18n.commonCancel')}
            />
        </>
    )

    return (
        <AudioCard
            audioId={`audio-card-${audio.id}`}
            variant={variant}
            isTabletUp={isTabletUp}
            coverSrc={audio.imageUrl}
            coverAlt={audio.title}
            title={audio.title}
            titleHref={getAudioPageUrl({
                authorUsername: audio.authorUsername,
                audioSlug: audio.slug,
            })}
            onTitleClick={handleTitleClick}
            authorLabel={audio.authorDisplayName}
            authorHref={getAuthorPageUrl({ authorUsername: audio.authorUsername })}
            onAuthorClick={handleAuthorClick}
            coverPlaySlot={
                <PlayButtonContainer
                    audio={audio}
                    placement={placement}
                    playlist={playlist}
                    variant="plain"
                    testID="cover-playback-button"
                />
            }
            bodySlot={
                <>
                    <PlayButtonContainer
                        audio={audio}
                        placement={placement}
                        playlist={playlist}
                        variant="colorful"
                    />
                    <WaveformContainer
                        audio={audio}
                        analyticsPlacement={placement}
                        playlist={playlist}
                        variant="dark"
                    />
                </>
            }
            statsSlot={
                <AudioPlayStats
                    plays={{ count: audio.playsCount, label: shortCount(audio.playsCount) }}
                    likes={
                        rendersAsOwner
                            ? { count: audio.likesCount, label: shortCount(audio.likesCount) }
                            : undefined
                    }
                />
            }
            tagsSlot={tagsSlot}
            timeSlot={<AudioPlaybackTimeContainer audioId={audio.id} durationMs={audio.duration} />}
            actionsSlot={withActions ? actionsSlot : undefined}
            followSlot={
                withActions ? (
                    <FollowButtonContainer
                        username={audio.authorUsername}
                        creatorUserId={audio.authorId}
                        placement={placement}
                        hideMobileText
                        size="tiny"
                        showForOwnProfile={isHydrating}
                    />
                ) : undefined
            }
            followMobileSlot={
                withActions ? (
                    <FollowButtonContainer
                        as="action"
                        username={audio.authorUsername}
                        creatorUserId={audio.authorId}
                        placement={placement}
                        showForOwnProfile={isHydrating}
                    />
                ) : undefined
            }
        />
    )
}
